import React, { useState, DragEvent } from 'react';
import { Clock, Save, X, Plus, List, Pencil, Check, ChevronDown, Edit3, Ungroup, Play, Flag, Square, Trash2, Undo2, Redo2 } from 'lucide-react';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import TimeElementCard, { RunRowModel } from './TimeElementCard';
import RowActionsMenu from './RowActionsMenu';
import utilityService, { DisplayElement } from '../../../shared/utils/utilityService';
import { useTime } from '../../../shared/context/TimeContext';
import timeSyncService from '../../sync/services/timeSyncService';
import { useExpandedGroups } from '../hooks/useExpandedGroups';
import { topLevelElements, childrenOf, isGroup, DropPosition } from '../utils/grouping';
import {
  absoluteSecondsToIsoInstant,
  formatVarianceMagnitude,
  formatVarianceWords,
  varianceGlyph,
  runPlannedBaselineSeconds,
  summarizeCompletedRun,
  varianceColorClass,
  varianceTone,
} from '../utils/runningState';
import {
  parseTimeToSeconds,
  formatTo12Hour,
  calculateRemainingSeconds,
  calculateExampleTimes,
  getUrgencyLevel,
  calculateElementDuration,
  getRunningElementTimings,
  plannedBaselineMap,
  summarizeVariance,
  Element,
  ElementInput,
  LocalizedRunningState,
  RunningElement,
  ObservationSource,
} from '@thekwallace/backtime-shared';

type ElementState = 'expired' | 'active' | 'upcoming';
type UrgencyLevel = 'imminent' | 'soon' | 'moderate' | 'distant';

interface EditedData {
  description?: string;
  duration?: number;
  anchorOffset?: number | null;
}

interface HeaderInfo {
  active: { description: string } | null;
  upcoming: { description: string; startTime: number } | null;
  upcomingCountdown: number | null;
}

interface TimeElementListProps {
  elements: Element[];
  selectedStartTime: string | null;
  occurrenceStartAbsolute?: number | null;
  timeOffset?: number;
  onAddElement: (element: Partial<ElementInput>) => void;
  onUpdateElement: (elementId: string, updates: Partial<ElementInput>) => void;
  onDeleteElement: (elementId: string) => void;
  onReorderElements: (fromIndex: number, toIndex: number) => void;
  onMoveElement?: (draggedId: string, targetId: string, position: DropPosition, groupName?: string) => void;
  onUngroupElement?: (groupId: string) => void;
  timelineId?: string | null;
  noUpcomingOccurrences?: boolean;
  isOccurrenceComplete?: boolean;
  dayName?: string | null;
  readOnly?: boolean;
  // ---- Running state (Operate Mode) ----
  // Owned by App so the hero countdown and this list project from ONE state and
  // cannot contradict each other. All optional: omit them and the list renders
  // exactly as it did before Operate Mode existed.
  //
  // ALREADY LOCALIZED into this browser's absolute-seconds frame — App converts
  // the wire instants once, for both consumers, so the two can never anchor
  // against different clocks. See `localizeRunningStateForBrowser`.
  runningState?: LocalizedRunningState | null;
  /** True while a running-state write is in flight. */
  runPending?: boolean;
  /** Last running-state write failure, or null. */
  runError?: string | null;
  onStartRun?: (occurrenceStart: string) => void;
  onMarkElement?: (
    elementId: string,
    actualStart?: string,
    source?: ObservationSource
  ) => void;
  onClearObservation?: (elementId: string) => void;
  onHoldElement?: (elementId: string) => void;
  onUndoLastObservation?: () => void;
  onRedoLastObservation?: () => void;
  onCompleteRun?: () => void;
  onAbandonRun?: () => void;
  onClearRunError?: () => void;
}

const TimeElementList: React.FC<TimeElementListProps> = ({
  elements,
  selectedStartTime,
  occurrenceStartAbsolute = null,
  timeOffset = 0,
  onAddElement,
  onUpdateElement,
  onDeleteElement,
  onMoveElement,
  onUngroupElement,
  timelineId = null,
  noUpcomingOccurrences = false,
  isOccurrenceComplete = false,
  readOnly = false,
  runningState = null,
  runPending = false,
  runError = null,
  onStartRun,
  onMarkElement,
  onClearObservation,
  onHoldElement,
  onUndoLastObservation,
  onRedoLastObservation,
  onCompleteRun,
  onAbandonRun,
  onClearRunError,
}) => {
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<EditedData>({});
  const [showAddForm, setShowAddForm] = useState(false);
  // What the user asked for. The EFFECTIVE mode (`editMode`, below) also requires
  // that no occurrence is running.
  const [editModeRequested, setEditModeRequested] = useState(false);
  const [newElementData, setNewElementData] = useState<EditedData>({ description: '', duration: 300 });
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: DropPosition } | null>(null);

  const { getTimeWithOffset } = useTime();
  const currentTimeSeconds = getTimeWithOffset(timeOffset);
  const { isExpanded, toggle } = useExpandedGroups(timelineId);

  const selectedOccurrenceSeconds = occurrenceStartAbsolute !== null
    ? occurrenceStartAbsolute
    : (!selectedStartTime || selectedStartTime.trim() === '')
      ? 0
      : parseTimeToSeconds(selectedStartTime);

  const currentForCalc = occurrenceStartAbsolute !== null
    ? currentTimeSeconds
    : ((currentTimeSeconds % 86400) + 86400) % 86400;

  const sortedElements = [...elements].sort((a, b) => a.sortOrder - b.sortOrder);

  // Timing takes the RAW element tree. `getElementTimingsFromElements` flattens groups
  // itself, in tree order, and keys top-level off `parentId == null`.
  //
  // Do NOT pre-flatten with `flattenLeafElements` here. That drops the group container
  // but leaves `parentId` set on its children, so the shared function's own flatten sees
  // them as orphans pointing at an element no longer in the array and filters every one
  // of them out. The group's whole duration then falls out of the chain: rows render with
  // no timing, and every downstream element — and every variance — shifts earlier by the
  // group's length. The API has always passed raw elements
  // (running-state.controller.ts), which is exactly why the server was right and this
  // client was wrong. Keep the two inputs identical.

  // ---- Running state (Operate Mode) ----
  // Occurrence liveness keys off RunningState.status, NOT
  // summarizeVariance().hasElementInProgress — that flag is false for a started-
  // but-unmarked occurrence, i.e. exactly when the operator is waiting on the
  // first cue and most needs the controls.
  const isRunning = runningState?.status === 'running';
  const isCompletedRun = runningState?.status === 'completed';
  // "A run exists" — the gate for every reconciliation below. NOT `isRunning`:
  // a completed run is still a run and must still render as one. An ABANDONED run
  // is different in kind — the server removes it outright and GET reports null —
  // so there is genuinely nothing to render and the planned view is correct.
  const runPhase: 'running' | 'completed' | undefined =
    isRunning ? 'running' : isCompletedRun ? 'completed' : undefined;
  const hasRun = runPhase !== undefined;

  // The planned baseline is pinned to the occurrence the run was STARTED against,
  // not to whatever the app currently has selected (plan §9.1). Auto-advance is on
  // by default with a 300s buffer, so the server rolls `activeStartTime` to the next
  // occurrence shortly after this one's planned end — i.e. while an over-running
  // service is still being operated. Without this pin every number on screen would
  // silently jump an hour at exactly the moment the operator needs them to hold
  // still, while the server kept reporting the truth.
  //
  // Taken from the LOCALIZED state, which is what makes the planned baseline
  // below share one frame AND one occurrence with the actuals it is compared
  // against (plan §5). The instant→seconds conversion this replaces was correct,
  // but it lived here and in CountdownTimer, and two copies of a frame decision
  // is one more than the number that can be kept in step.
  //
  // `runPlannedBaselineSeconds` strips `timeOffset` back off: the localizer put
  // it on every instant so observations share a frame with `currentForCalc`, but
  // the SCHEDULE does not move when the operator nudges their clock. Note
  // `selectedOccurrenceSeconds` above carries no offset either — the two branches
  // mean the same thing and coincide when offset is 0.
  const occurrenceStartSeconds = hasRun && runningState
    ? runPlannedBaselineSeconds(runningState, timeOffset)
    : selectedOccurrenceSeconds;

  const { elements: plannedDisplayElements } = utilityService.getAllElements(
    currentForCalc,
    occurrenceStartSeconds,
    elements
  );

  // Called UNCONDITIONALLY. `getRunningElementTimings(planned, null, now)` returns the
  // planned timings verbatim with variance 0, so a timeline with nothing running renders
  // byte-for-byte as it did before Operate Mode existed. That equivalence is the single
  // most important regression guard in this feature — see runningState.test.ts.
  //
  // The fourth argument is the BASELINE — the plan as it stood when Start was pressed —
  // and it is what stops a mid-run edit rewriting history. `plannedDisplayElements` above
  // is built from the CURRENT elements, which is correct for the chain: trimming
  // something downstream should move when things happen. But planned mode presses
  // chained elements backward into the next anchor, so measuring variance against those
  // same current timings moves the planned start of everything BEFORE the edit — and an
  // element that began 30s late then reports "1:30 ahead". Undefined when no run has a
  // snapshot, in which case the projection falls back to the planned input exactly as
  // before.
  const runningTimings = getRunningElementTimings(
    plannedDisplayElements,
    runningState,
    currentForCalc,
    plannedBaselineMap(runningState, occurrenceStartSeconds)
  );
  const varianceSummary = summarizeVariance(runningTimings);
  // Every figure in `varianceSummary` derives from the element with status
  // 'current', and a completed run has none — so the summary is all zeros once the
  // run ends. The end-of-run numbers have to come from the per-element data.
  const outcome = isCompletedRun ? summarizeCompletedRun(runningTimings) : null;

  const allDisplayElements: DisplayElement[] = runningTimings.map((el) => ({
    ...el,
    countdown: calculateRemainingSeconds(currentForCalc, el.startTime),
    isAnchored: el.anchorOffset !== null,
  }));
  const elementsById: Record<string, DisplayElement> = {};
  allDisplayElements.forEach((el) => { elementsById[el.id] = el; });

  const runningById: Record<string, RunningElement> = {};
  runningTimings.forEach((el) => { runningById[el.id] = el; });

  // The next leaf the operator has not yet marked. Elements already marked are
  // 'completed'/'current'; ones the operator ran past without marking are 'skipped'.
  // The playhead follows the clock now, so "where are we" is read off the projection
  // rather than off the last thing the operator said.
  const playheadIndex = runningTimings.findIndex((el) => el.status === 'current');
  // Read from the projection rather than inferred from a timing coincidence — two
  // surfaces inferring it separately is how the hero and the list came to disagree.
  const heldElement = runningTimings.find((el) => el.held);
  /** The element the run would have moved on from — the only one it makes sense to hold. */
  const holdCandidateId =
    isRunning && playheadIndex > 0 ? runningTimings[playheadIndex - 1].id : null;

  const nextToMark = isRunning
    ? runningTimings.find((el) => el.status === 'upcoming') ?? null
    : null;

  // Editing is disabled outright while an occurrence is running (plan §9.1) — it
  // removes the whole class of "elements changed under a live occurrence" bugs.
  // Gating the single derived `editMode` here means every downstream affordance
  // (drag, add form, inline edit, row menu) is locked by construction rather than
  // by remembering to check at each call site.
  // Play mode DIVIDES the timeline rather than locking it. Everything the run has not
  // reached stays freely editable; an element it has reached must be CLEARED first.
  //
  // This is not a relaxation of the old rule so much as a correction of its scope. The
  // lock existed to stop an observation pointing at an element that was deleted,
  // reordered or re-parented underneath it — so the elements at risk are exactly those
  // that HAVE an observation, which are exactly those at or before the playhead.
  // "Locked iff observed" and "locked iff reached" are the same set, and `clear` is
  // what moves an element from one to the other. The blanket lock was simply broader
  // than the invariant ever required.
  const editMode = editModeRequested;
  /** True for a row whose recorded start would be invalidated by editing it. */
  const rowIsLocked = (elementId: string): boolean =>
    isRunning && runningById[elementId]?.actualStart != null;

  const handleStart = () => {
    if (occurrenceStartAbsolute === null || !onStartRun) return;
    const occurrenceStart = absoluteSecondsToIsoInstant(
      occurrenceStartAbsolute,
      timeSyncService.getSyncedTimeSeconds(0),
      timeSyncService.getSyncedTimeMs()
    );
    onStartRun(occurrenceStart);
  };

  // No `actualStart`: the server stamps a live mark from its own clock in the
  // TIMELINE's timezone. `currentForCalc` is seconds from the BROWSER's local
  // midnight and additionally carries the user's display `timeOffset`, so sending
  // it would skew the record for any operator outside the timeline's timezone.
  // (Nothing here writes back to Timeline.timeOffset — the two are unrelated.)
  const handleMark = (elementId: string) => { onMarkElement?.(elementId); };

  /**
   * Shortest sensible element. Nothing stops the schema storing less, but a stepper
   * that can walk an element to zero invites an accidental tap doing it, and a
   * 0-duration element is meaningless on a run sheet.
   */
  const MIN_DURATION_SECONDS = 30;

  /**
   * Make up time by trimming something the run has not reached yet.
   *
   * The operator's actual move when they are running behind. It is an edit rather than
   * a property of the element: no schema field could tell the app which elements are
   * compressible without someone first classifying every element of every timeline,
   * and the arithmetic would be identical either way.
   *
   * Safe under a live run because it changes no element IDENTITY — the lock exists to
   * stop deletes and reorders breaking the observation-to-element mapping, not to
   * freeze numbers. Planned timings are derived from the current elements on every
   * render, so the forward chain, the anchor risk and the hero all recompute at once.
   */
  const handleAdjustDuration = (elementId: string, currentDuration: number, deltaSeconds: number) => {
    const next = Math.max(MIN_DURATION_SECONDS, currentDuration + deltaSeconds);
    if (next === currentDuration) return;
    onUpdateElement(elementId, { duration: next });
  };

  /**
   * Correct a recorded start in place, by appending a superseding observation.
   *
   * Not a separate concept: a correction IS a mark carrying an explicit instant, which
   * is why this needs no API work. Because the projection is a pure fold over an
   * append-only log, one appended record shifts everything downstream coherently, and
   * the operator never has to reason about what else moves.
   */
  const handleCorrectStart = (elementId: string, actualStartSeconds: number, deltaSeconds: number) => {
    if (!onMarkElement) return;
    onMarkElement(
      elementId,
      absoluteSecondsToIsoInstant(
        actualStartSeconds + deltaSeconds,
        timeSyncService.getSyncedTimeSeconds(timeOffset),
        timeSyncService.getSyncedTimeMs()
      ),
      // Declared as a CORRECTION, not a live mark. On the wire these were previously
      // indistinguishable, which is why the projection's cursor rule has to guess
      // whether an observation moves the operator's position. A correction never
      // should; a mark always does.
      'correction'
    );
  };

  const handleComplete = () => { onCompleteRun?.(); };

  // Discarding a LIVE run throws away everything the operator has marked, and the
  // control sits next to Mark during a service. Confirm that one; discarding an
  // already-completed run only clears a finished readout, so it just goes.
  const handleAbandon = () => {
    if (isRunning && !window.confirm('Discard this run? Everything marked so far is lost.')) return;
    onAbandonRun?.();
  };

  // "Next" while running is the next element to MARK, not whichever projected
  // start happens to be nearest the clock. Same single-source-of-truth rule as
  // getElementState below.
  // Nothing is "next" once the run has ended.
  const nextElementId = hasRun
    ? (isRunning ? nextToMark?.id ?? null : null)
    : utilityService.findNextElementId(allDisplayElements);

  const showActualTimes = Boolean(selectedStartTime && selectedStartTime.trim() !== '');
  const showCountdowns = !noUpcomingOccurrences && !isOccurrenceComplete && showActualTimes
    && !isCompletedRun;

  /**
   * Which clock-state a row renders in.
   *
   * WHILE RUNNING, `runStatus` WINS (plan §9.1). The legacy derivation below
   * compares an element's projected window against the clock, which during a run
   * disagrees with what the operator actually marked: an unmarked element whose
   * projected window contained "now" rendered as "Happening Now" with a running
   * progress bar, while the element the operator had genuinely marked rendered
   * inert and styled expired. Two rows appeared live and the real one showed no
   * timing at all.
   *
   * Reconciled here rather than in the card so there is exactly one definition of
   * "which row is live", and every consumer of `state` (badge, border colour,
   * progress bar, countdown visibility, "Starts at"/"Started at") follows it.
   *
   * When nothing is running this is byte-for-byte the original behaviour.
   */
  const getElementState = (elementId: string): ElementState => {
    if (hasRun) {
      switch (runningById[elementId]?.status) {
        case 'current': return 'active';
        case 'completed':
        case 'skipped': return 'expired';
        // Never reached. While the run is live it is genuinely still upcoming;
        // once the run has ended it never happened, and rendering it 'upcoming'
        // is what produced "Up Next 21:29" underneath an "Occurrence complete"
        // banner.
        default: return isCompletedRun ? 'expired' : 'upcoming';
      }
    }
    if (noUpcomingOccurrences) return 'upcoming';
    const element = elementsById[elementId];
    if (!element) return 'upcoming';
    return utilityService.getElementState(element, currentForCalc);
  };

  const getUrgency = (elementId: string): UrgencyLevel => {
    if (noUpcomingOccurrences) return 'distant';
    const element = elementsById[elementId];
    if (!element || element.countdown <= 0) return 'moderate';
    return getUrgencyLevel(element.countdown) as UrgencyLevel;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatStartTime = (element: Element): string => {
    const displayElement = elementsById[element.id];
    return noUpcomingOccurrences && displayElement
      ? calculateExampleTimes(displayElement.startTime - occurrenceStartSeconds)
      : showActualTimes && displayElement
        ? formatTo12Hour(displayElement.startTime, true)
        : `+${formatDuration(displayElement?.startTime ?? 0)}`;
  };

  // Editing
  const handleStartEdit = (element: Element) => {
    // Belt and braces: the affordance is withheld for a locked row, but refusing here
    // means a stale render or a keyboard path cannot get around it.
    if (rowIsLocked(element.id)) return;
    setEditingElementId(element.id);
    setEditedData({ description: element.description, duration: element.duration ?? 0, anchorOffset: element.anchorOffset });
  };
  const handleSaveEdit = () => {
    if (!editingElementId) return;
    onUpdateElement(editingElementId, { description: editedData.description, duration: editedData.duration, anchorOffset: editedData.anchorOffset });
    setEditingElementId(null);
    setEditedData({});
  };
  const handleCancelEdit = () => { setEditingElementId(null); setEditedData({}); };

  // Edit mode reveals row reordering, the Add form, and per-row (⋯) actions.
  // Leaving edit mode dismisses any open inline form so operate mode stays clean.
  const toggleEditMode = () => {
    setEditModeRequested((on) => {
      if (on) { setShowAddForm(false); setEditingElementId(null); setEditedData({}); }
      return !on;
    });
  };

  // Groups only have a name to edit (duration is calculated, no anchor) — save just the description.
  const handleSaveGroupRename = () => {
    if (!editingElementId) return;
    onUpdateElement(editingElementId, { description: editedData.description });
    setEditingElementId(null);
    setEditedData({});
  };

  const handleAddNewElement = () => {
    onAddElement({
      description: newElementData.description || 'New Element',
      duration: newElementData.duration || 300,
      anchorOffset: newElementData.anchorOffset ?? null
    });
    setNewElementData({ description: '', duration: 300 });
    setShowAddForm(false);
  };

  // Drag-and-drop: thirds (before / into / after). `into` only offered for top-level targets.
  const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>, id: string, allowInto: boolean) => {
    e.preventDefault();
    if (id === draggedId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let position: DropPosition;
    if (allowInto && y > rect.height / 3 && y < (rect.height * 2) / 3) position = 'into';
    else position = y < rect.height / 2 ? 'before' : 'after';
    setDropTarget({ id, position });
  };
  const handleDragEnd = () => {
    if (draggedId && dropTarget && draggedId !== dropTarget.id && onMoveElement) {
      if (dropTarget.position === 'into') {
        const target = elements.find((el) => el.id === dropTarget.id);
        const targetIsGroup = target ? isGroup(elements, target) : false;
        if (targetIsGroup) {
          onMoveElement(draggedId, dropTarget.id, 'into');
        } else {
          const name = window.prompt('Name this group:', 'Group');
          if (name !== null) onMoveElement(draggedId, dropTarget.id, 'into', name.trim() || 'Group');
        }
      } else {
        onMoveElement(draggedId, dropTarget.id, dropTarget.position);
      }
    }
    setDraggedId(null);
    setDropTarget(null);
  };

  const dropClasses = (id: string): string => {
    if (dropTarget?.id !== id) return '';
    if (dropTarget.position === 'before') return 'border-t-2 border-blue-500';
    if (dropTarget.position === 'after') return 'border-b-2 border-blue-500';
    return 'ring-2 ring-blue-500 rounded-lg'; // into
  };

  const getHeaderInfo = (): HeaderInfo | null => {
    if (sortedElements.length === 0) return null;

    // The collapsed header ran the same window-vs-clock derivation as the rows and
    // therefore had the same defect: while running it would name whichever element's
    // PROJECTED window contained "now" as happening, rather than the one the operator
    // marked. One rule, applied everywhere.
    if (hasRun) {
      const current = runningTimings.find((el) => el.status === 'current') ?? null;
      const upcoming = isRunning ? nextToMark : null;
      const remaining = upcoming ? calculateRemainingSeconds(currentForCalc, upcoming.startTime) : 0;
      return {
        active: current ? { description: current.description } : null,
        upcoming: upcoming && remaining > 0 ? upcoming : null,
        upcomingCountdown: remaining > 0 ? remaining : null,
      };
    }

    const nextElement = utilityService.getNextTimeElement(currentForCalc, occurrenceStartSeconds, elements);
    let activeElement: { description: string } | null = null;
    for (const el of allDisplayElements) {
      if (utilityService.getElementState(el, currentForCalc) === 'active') { activeElement = { description: el.description }; break; }
    }
    let upcomingCountdown: number | null = null;
    if (nextElement) {
      const remaining = calculateRemainingSeconds(currentForCalc, nextElement.startTime);
      if (remaining > 0) upcomingCountdown = remaining;
    }
    return {
      active: activeElement,
      upcoming: nextElement && upcomingCountdown && upcomingCountdown > 0 ? nextElement : null,
      upcomingCountdown
    };
  };

  // ---- Render helpers ----

  const renderEditForm = (element: Element) => (
    <div key={element.id} className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-blue-500">
      <div className="space-y-4">
        <input
          type="text"
          placeholder="Description"
          value={editedData.description || ''}
          onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
          className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Duration (MM:SS)</label>
            <div className="flex items-center gap-1">
              <input type="number" min="0" value={Math.floor((editedData.duration || 0) / 60)}
                onChange={(e) => { const minutes = parseInt(e.target.value) || 0; const cur = (editedData.duration || 0) % 60; setEditedData({ ...editedData, duration: minutes * 60 + cur }); }}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
              <span className="text-gray-400 text-lg font-bold">:</span>
              <input type="number" min="0" max="59" value={(editedData.duration || 0) % 60}
                onChange={(e) => { const seconds = Math.min(59, Math.max(0, parseInt(e.target.value) || 0)); const curM = Math.floor((editedData.duration || 0) / 60); setEditedData({ ...editedData, duration: curM * 60 + seconds }); }}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Anchor offset</label>
            <div className="flex space-x-2">
              <input type="number" placeholder="Min"
                value={editedData.anchorOffset !== null && editedData.anchorOffset !== undefined ? Math.floor(editedData.anchorOffset / 60) : ''}
                onChange={(e) => { const v = e.target.value; if (v === '' && ((editedData.anchorOffset ?? 0) % 60) === 0) setEditedData({ ...editedData, anchorOffset: null }); else { const m = parseInt(v) || 0; const s = (editedData.anchorOffset ?? 0) % 60; setEditedData({ ...editedData, anchorOffset: m * 60 + s }); } }}
                className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
              <input type="number" min="0" max="59" placeholder="Sec"
                value={editedData.anchorOffset !== null && editedData.anchorOffset !== undefined ? (editedData.anchorOffset % 60) : ''}
                onChange={(e) => { const v = e.target.value; if (v === '' && Math.floor((editedData.anchorOffset ?? 0) / 60) === 0) setEditedData({ ...editedData, anchorOffset: null }); else { const s = Math.min(59, Math.max(0, parseInt(v) || 0)); const m = Math.floor((editedData.anchorOffset ?? 0) / 60); setEditedData({ ...editedData, anchorOffset: m * 60 + s }); } }}
                className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <button onClick={handleSaveEdit} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center justify-center">
            <Save className="w-4 h-4 mr-2" /> Save
          </button>
          <button onClick={handleCancelEdit} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // A single leaf element row (draggable). `isChild` rows can't be a group target.
  const renderCard = (element: Element, isChild: boolean) => {
    if (editMode && editingElementId === element.id) return renderEditForm(element);
    const displayElement = elementsById[element.id];
    const running = runningById[element.id];

    // What the RUN says about this row, as opposed to what the schedule says.
    // Absent entirely outside a run, so the planned view is untouched.
    const isAnchorRow = element.anchorOffset !== null;
    const risk = varianceSummary.anchorsAtRisk.find((a) => a.elementId === element.id);
    const runRow: RunRowModel | undefined = hasRun && running
      ? {
          ...(running.status === 'current' && running.actualStart !== null
            ? {
                elapsed: formatDuration(Math.max(0, Math.round(currentForCalc - running.actualStart))),
                budget: formatDuration(running.duration),
                dueToEndClock: formatTo12Hour(running.actualStart + running.duration, true),
                progress: running.duration > 0
                  ? (currentForCalc - running.actualStart) / running.duration
                  : 0,
                ...(currentForCalc - running.actualStart > running.duration
                  ? { overBy: formatDuration(Math.round(currentForCalc - running.actualStart - running.duration)) }
                  : { remaining: formatDuration(Math.round(running.actualStart + running.duration - currentForCalc)) }),
              }
            : {}),
          ...(running.status === 'completed' && running.actualStart !== null
            ? {
                // MEASURED, not assumed: the projection sets a completed element's end
                // to the next observed start, so this is what actually elapsed.
                ranFor: formatDuration(Math.max(0, Math.round(running.endTime - running.actualStart))),
                spanFrom: formatTo12Hour(running.actualStart, true),
                spanTo: formatTo12Hour(running.endTime, true),
              }
            : {}),
          ...(isAnchorRow && isRunning
            ? {
                anchorClock: formatTo12Hour(running.plannedStart, true),
                anchorRisk: risk
                  ? `current pace arrives ${formatVarianceMagnitude(risk.variance)} late`
                  : null,
              }
            : {}),
          // Only a forward-looking, movable start gets the tilde. Anchors hold, and a
          // row the run has already passed is history, not a forecast.
          projected: isRunning && !isAnchorRow && running.status === 'upcoming',
        }
      : undefined;
    return (
      <div
        key={element.id}
        draggable={!readOnly && editMode}
        onDragStart={(e) => handleDragStart(e, element.id)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => { if (!rowIsLocked(element.id)) handleDragOver(e, element.id, !isChild); }}
        className={`transition-all ${draggedId === element.id ? 'opacity-50' : ''} ${dropClasses(element.id)}`}
      >
        <TimeElementCard
          element={{
            id: element.id,
            description: element.description,
            duration: element.duration ?? 0,
            startTime: displayElement?.startTime ?? 0,
            endTime: displayElement?.endTime ?? 0,
            // Undefined, never 0: a row with no projection has nothing to count down,
            // and rendering "0:00" made three songs look imminent when in fact the
            // client had lost them entirely.
            countdown: displayElement?.countdown,
          }}
          state={getElementState(element.id)}
          isNextUp={element.id === nextElementId}
          urgency={getUrgency(element.id)}
          showCountdown={showCountdowns}
          showActualTimes={showActualTimes}
          formattedStartTime={formatStartTime(element)}
          currentTime={currentForCalc}
          onEdit={!readOnly ? () => handleStartEdit(element) : undefined}
          onDelete={!readOnly ? () => onDeleteElement(element.id) : undefined}
          draggable={!readOnly && editMode && !rowIsLocked(element.id)}
          editMode={editMode}
          locked={rowIsLocked(element.id)}
          onClear={
            !readOnly && isRunning && rowIsLocked(element.id) && onClearObservation
              ? () => onClearObservation(element.id)
              : undefined
          }
          variance={running?.variance}
          runPhase={runPhase}
          runStatus={running?.status}
          onMark={!readOnly && isRunning ? () => handleMark(element.id) : undefined}
          rowIsMarkTarget={
            // Press records a START, so it is offered only where that is meaningful:
            // the element in progress or one still ahead, and only when nothing has been
            // recorded for it yet. Saying it again would rewrite a time the operator
            // already gave us; the stepper is how that gets adjusted.
            !readOnly && isRunning && running?.actualStart == null
              && (running?.status === 'current' || running?.status === 'upcoming')
          }
          onHold={
            !readOnly && isRunning && onHoldElement && element.id === holdCandidateId
              ? () => onHoldElement(element.id)
              : undefined
          }
          isHeld={heldElement?.id === element.id}
          heldOverBy={
            heldElement?.id === element.id
              ? formatDuration(Math.max(0, Math.round(
                  currentForCalc - (running!.startTime + running!.duration)
                )))
              : null
          }
          runRow={runRow}
          onCorrectStart={
            // Available on the element in progress whether or not anything has been
            // recorded for it. Under "assume the plan is being followed" most rows have
            // no recorded start, so gating on one would hide the control exactly where
            // it is wanted — the first adjustment is what creates the record, seeded
            // from the assumed start.
            !readOnly && isRunning && running
              && (running.actualStart !== null || running.status === 'current')
              ? (delta: number) =>
                  handleCorrectStart(element.id, running.actualStart ?? running.startTime, delta)
              : undefined
          }
          onAdjustDuration={
            // Only forwards: an element the run has already reached has happened, and
            // its length is now a measurement rather than a plan.
            !readOnly && isRunning && running?.status === 'upcoming' && element.duration !== null
              ? (delta: number) => handleAdjustDuration(element.id, element.duration as number, delta)
              : undefined
          }
          offsetInfo={element.anchorOffset !== null
            ? `Anchored at +${Math.floor(element.anchorOffset / 60)}m${element.anchorOffset % 60 > 0 ? ` ${element.anchorOffset % 60}s` : ''} from occurrence start`
            : undefined}
        />
      </div>
    );
  };

  // A group renders like a normal element row (so collapsed it reads as one element),
  // with a disclosure chevron + ungroup action, and is draggable as a whole.
  const renderGroup = (group: Element) => {
    // Rename form (groups only edit their name)
    if (editMode && editingElementId === group.id) {
      return (
        <div key={group.id} className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-blue-500">
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Group name"
              value={editedData.description || ''}
              onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex space-x-2">
              <button onClick={handleSaveGroupRename} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center justify-center">
                <Save className="w-4 h-4 mr-2" /> Save
              </button>
              <button onClick={handleCancelEdit} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    const kids = childrenOf(elements, group.id);
    const kidDisplays = kids.map((k) => elementsById[k.id]).filter(Boolean) as Array<{ startTime: number; endTime: number }>;
    // Groups are visual-only: their running state DERIVES from their leaves, and the
    // operator marks leaves, never groups (plan §5.3). A group's variance is its last
    // leaf's — that is where the group as a whole lands relative to plan.
    const kidRunning = kids.map((k) => runningById[k.id]).filter(Boolean) as RunningElement[];
    const groupVariance = kidRunning.length ? kidRunning[kidRunning.length - 1].variance : undefined;
    // A group's run status derives from its leaves: in progress if any leaf is,
    // otherwise finished if any leaf actually ran, otherwise still ahead of us.
    const groupRunStatus = kidRunning.find((k) => k.status === 'current')?.status
      ?? (kidRunning.some((k) => k.actualStart !== null) ? 'completed' : 'upcoming');
    const groupDuration = calculateElementDuration({ ...group, children: kids });
    const expanded = isExpanded(group.id);
    const startTime = kidDisplays.length ? Math.min(...kidDisplays.map((d) => d.startTime)) : 0;
    const endTime = kidDisplays.length ? Math.max(...kidDisplays.map((d) => d.endTime)) : startTime;
    const groupCountdown = Math.max(0, calculateRemainingSeconds(currentForCalc, startTime));
    // A group derives from its leaves (plan §5.3), so while running it must derive
    // from their RUN statuses for the same reason getElementState does — otherwise
    // a collapsed group and its expanded children disagree about being live.
    const groupState: ElementState = hasRun
      ? kidRunning.some((k) => k.status === 'current')
        ? 'active'
        : isCompletedRun
          || (kidRunning.length > 0 && kidRunning.every((k) => k.status === 'completed' || k.status === 'skipped'))
          ? 'expired'
          : 'upcoming'
      : noUpcomingOccurrences
        ? 'upcoming'
        : currentForCalc < startTime ? 'upcoming' : currentForCalc < endTime ? 'active' : 'expired';
    // Mirror the leaf getUrgency() so a collapsed group colors consistently with its children:
    // no upcoming occurrence => distant (gray), not the moderate (green) default.
    const groupUrgency: UrgencyLevel = noUpcomingOccurrences
      ? 'distant'
      : groupCountdown <= 0
        ? 'moderate'
        : (getUrgencyLevel(groupCountdown) as UrgencyLevel);
    const groupIsNextUp = kids.some((k) => k.id === nextElementId);
    const groupFormattedStart = noUpcomingOccurrences
      ? calculateExampleTimes(startTime - occurrenceStartSeconds)
      : showActualTimes ? formatTo12Hour(startTime, true) : `+${formatDuration(startTime)}`;

    // Collapsed: the group reads as a single time-element card (group-span timing/state),
    // with a right-facing disclosure chevron. Click the chevron to expand.
    if (!expanded) {
      return (
        <div key={group.id}>
          <div
            draggable={!readOnly && editMode}
            onDragStart={(e) => handleDragStart(e, group.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, group.id, true)}
            className={`transition-all ${draggedId === group.id ? 'opacity-50' : ''} ${dropClasses(group.id)}`}
          >
            <TimeElementCard
              element={{ id: group.id, description: group.description, duration: groupDuration, startTime, endTime, countdown: groupCountdown }}
              state={groupState}
              isNextUp={groupIsNextUp}
              urgency={groupUrgency}
              showCountdown={showCountdowns}
              showActualTimes={showActualTimes}
              formattedStartTime={groupFormattedStart}
              currentTime={currentForCalc}
              draggable={!readOnly && editMode}
              editMode={editMode}
              variance={groupVariance}
              runPhase={runPhase}
              runStatus={groupRunStatus}
              isGroup
              expanded={false}
              onToggleExpand={() => toggle(group.id)}
              onEdit={!readOnly ? () => handleStartEdit(group) : undefined}
              onUngroup={!readOnly && onUngroupElement ? () => onUngroupElement(group.id) : undefined}
              itemCount={kids.length}
            />
          </div>
        </div>
      );
    }

    // Expanded: drop the summary card — show just a down-chevron + connecting line and the
    // children's own cards. Clicking the chevron (or the group label) collapses back to the card.
    return (
      <div key={group.id}>
        <div className="flex items-center gap-1.5 mb-2 pl-1">
          <button
            onClick={() => toggle(group.id)}
            aria-label="Collapse group"
            aria-expanded={true}
            className="group flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{group.description}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">· {formatDuration(groupDuration)}</span>
          </button>
          {editMode && !readOnly && (
            <div className="flex items-center gap-0.5">
              <button onClick={() => handleStartEdit(group)} title="Rename group" aria-label="Rename group"
                className="p-1.5 text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all">
                <Edit3 className="w-4 h-4" />
              </button>
              {onUngroupElement && (
                <button onClick={() => onUngroupElement(group.id)} title="Ungroup" aria-label="Ungroup"
                  className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all">
                  <Ungroup className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {/* Children with a connecting line down the left edge */}
        <div className="ml-2 sm:ml-3 pl-3 border-l-2 border-gray-300 dark:border-gray-600 space-y-2">
          {kids.map((child) => renderCard(child, true))}
        </div>
      </div>
    );
  };

  const tree = topLevelElements(elements);

  return (
    <CollapsibleSection
      title="Schedule"
      icon={<List className="w-6 h-6 text-blue-400" />}
      defaultOpen={true}
      hideHeaderContentWhenOpen={true}
      headerContent={
        sortedElements.length > 0 ? (() => {
          if (showActualTimes && (isOccurrenceComplete || isCompletedRun)) {
            return <div className="flex items-center gap-2 text-xs sm:text-sm"><span className="text-gray-400">Occurrence complete</span></div>;
          }
          if (showCountdowns) {
            const headerInfo = getHeaderInfo();
            if (!headerInfo) return null;
            return (
              <div className="flex flex-col gap-1">
                {headerInfo.active && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <span className="text-gray-900 dark:text-white font-semibold">{headerInfo.active.description}</span>
                    <span className="text-green-500 dark:text-green-400">is happening now.</span>
                  </div>
                )}
                {headerInfo.upcoming && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <span className="text-gray-900 dark:text-white font-semibold">{headerInfo.upcoming.description}</span>
                    <span className="text-gray-500 dark:text-gray-400">is up next in:</span>
                    <span className="text-gray-900 dark:text-white font-mono font-bold">{utilityService.formatCountdown(headerInfo.upcomingCountdown ?? 0)}</span>
                  </div>
                )}
              </div>
            );
          }
          if (noUpcomingOccurrences && allDisplayElements.length > 0) {
            const firstElement = allDisplayElements[0];
            const exampleTime = calculateExampleTimes(firstElement.startTime - occurrenceStartSeconds);
            return (
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-gray-900 dark:text-white font-semibold">{firstElement.description}</span>
                <span className="text-gray-500 dark:text-gray-400">starts at</span>
                <span className="text-gray-900 dark:text-white font-mono">{exampleTime}</span>
              </div>
            );
          }
          return <div className="flex items-center gap-2 text-xs sm:text-sm"><span className="text-gray-500 dark:text-gray-400">{sortedElements.length} element{sortedElements.length !== 1 ? 's' : ''}</span></div>;
        })() : null
      }
      headerRight={
        !readOnly && (
          <div className="flex items-center gap-2">
            {/* ---- Operate mode: Start / Mark. Hidden while editing so the two
                   modes never present competing primary actions. ---- */}
            {!editMode && !isRunning && (
              <button
                onClick={(e) => { e.stopPropagation(); handleStart(); }}
                disabled={!timelineId || occurrenceStartAbsolute === null || runPending}
                aria-label="Start operating this occurrence"
                title={
                  !timelineId
                    ? 'Save this timeline before operating an occurrence'
                    : occurrenceStartAbsolute === null
                      ? 'No scheduled occurrence to operate'
                      : 'Start operating this occurrence'
                }
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600"
              >
                <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">Start</span>
              </button>
            )}
            {!editMode && isRunning && (
              <button
                onClick={(e) => { e.stopPropagation(); if (nextToMark) handleMark(nextToMark.id); }}
                disabled={!nextToMark || runPending}
                aria-label={nextToMark ? `Mark ${nextToMark.description} as started` : 'Nothing left to mark'}
                title={nextToMark ? `Mark ${nextToMark.description} as started` : 'Nothing left to mark'}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                <Flag className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium truncate max-w-[9rem]">
                  {nextToMark ? `Mark: ${nextToMark.description}` : 'Mark'}
                </span>
              </button>
            )}
            {/* End the run. Without this, pressing Start locked editing with no way
                out (plan §10) — never ship a Start control without a way to end it. */}
            {!editMode && isRunning && (
              <button
                onClick={(e) => { e.stopPropagation(); handleComplete(); }}
                disabled={runPending}
                aria-label="Complete this occurrence"
                title="Complete this occurrence"
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 bg-gray-700 hover:bg-gray-800 text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-700"
              >
                <Square className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">Complete</span>
              </button>
            )}
            {/* Discard is destructive and irreversible, and it sat one button away from
                Mark — the most-tapped control in the app, pressed repeatedly under time
                pressure. While the run is LIVE it moves behind an overflow.
                On a COMPLETED run it stays prominent: clearing a finished run off
                viewers' screens is its everyday use, and nothing is lost by then. */}
            {!editMode && isRunning && (
              <div onClick={(e) => e.stopPropagation()}>
                <RowActionsMenu
                  label="Run actions"
                  disabled={runPending}
                  actions={[{
                    label: 'Discard run',
                    danger: true,
                    icon: <Trash2 className="w-4 h-4" />,
                    onClick: handleAbandon,
                  }]}
                />
              </div>
            )}
            {!editMode && isCompletedRun && (
              <button
                onClick={(e) => { e.stopPropagation(); handleAbandon(); }}
                disabled={runPending}
                aria-label="Discard this occurrence"
                title="Discard this occurrence"
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-600"
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">Discard</span>
              </button>
            )}
            {/* Add Element — only available while editing */}
            {editMode && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowAddForm(!showAddForm); }}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-white rounded-lg transition-all duration-200 transform hover:scale-105 ${showAddForm ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {showAddForm ? (
                  <><X className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Cancel</span></>
                ) : (
                  <><Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium hidden xs:inline">Add Element</span><span className="text-xs sm:text-sm font-medium xs:hidden">Add</span></>
                )}
              </button>
            )}
            {/* Edit / Done toggle: switches between operate and edit modes. Available
                DURING a run now — the lock moved onto the individual rows the run has
                reached, which is the only place the data model ever required it. */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleEditMode(); }}
              aria-pressed={editMode}
              title={
                isRunning
                  ? 'Rows the run has reached are locked until cleared'
                  : undefined
              }
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${editMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'}`}
            >
              {editMode ? (
                <><Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Done</span></>
              ) : (
                <><Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Edit</span></>
              )}
            </button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {/* Running-state banner. Every variance below is written as sign + magnitude +
            word ("+0:45 behind"), so the meaning survives with no colour perception at
            all — the colour is reinforcement only. */}
        {(isRunning || isCompletedRun) && (() => {
          // The headline is `currentVariance` — live drift at the current element's
          // hand-off, which MOVES while an element over-runs. Not
          // `projectedEndVariance`: that reads literally as "will the occurrence
          // FINISH late?" and is structurally 0 whenever an anchor sits between the
          // cursor and the last element, so a run 3 minutes behind can report it as
          // 0 all the way to the end. It is shown below, labelled for what it is,
          // and only when it has something to say.
          const currentTone = varianceTone(varianceSummary.currentVariance);
          const startTone = varianceTone(varianceSummary.currentStartVariance);
          const overrun = varianceSummary.currentVariance - varianceSummary.currentStartVariance;
          return (
            <div
              role="status"
              aria-live="polite"
              className="rounded-xl border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-blue-700 dark:text-blue-300">
                  <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-blue-600 dark:bg-blue-400' : 'bg-gray-400 dark:bg-gray-500'}`} aria-hidden="true" />
                  {isRunning ? 'Occurrence running' : 'Occurrence complete'}
                </span>
                {varianceSummary.hasElementInProgress ? (
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                    Now:{' '}
                    <span className={`font-mono font-bold ${varianceColorClass(currentTone)}`}>
                      <span aria-hidden="true">{varianceGlyph(currentTone)} </span>
                      {formatVarianceWords(varianceSummary.currentVariance)}
                    </span>
                  </span>
                ) : null}

                {/* How the run FINISHED. Not hedged the way a live "ahead" reading
                    is: nothing is being projected any more, so this is elapsed fact.
                    Measured at the last element that actually ran. */}
                {outcome && outcome.lastRan && (
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                    Ended{' '}
                    <span className={`font-mono font-bold ${varianceColorClass(varianceTone(outcome.finalVariance))}`}>
                      {formatVarianceWords(outcome.finalVariance)}
                    </span>
                    {' '}on <span className="font-medium">{outcome.lastRan.description}</span>
                  </span>
                )}
                {outcome && (
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {outcome.ran} of {runningTimings.length} element{runningTimings.length !== 1 ? 's' : ''} run
                    {outcome.reported < outcome.ran ? `, ${outcome.reported} reported` : ''}
                    {outcome.notReached > 0 ? `, ${outcome.notReached} not reached` : ''}
                  </span>
                )}
                {/* Started-late and running-long are different problems with
                    different remedies, so they are never collapsed into one figure. */}
                {varianceSummary.hasElementInProgress && startTone !== 'on-time' && varianceTone(overrun) !== 'on-time' && (
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    Started {formatVarianceWords(varianceSummary.currentStartVariance)}, then{' '}
                    {formatVarianceWords(overrun)}
                  </span>
                )}
              </div>

              {/* Undo — drop the last observation.
                  Live only, and only when there is something to drop. Cheap and total
                  because the log is append-only and every projection is a fold over it:
                  dropping the last entry restores the previous state exactly. It undoes
                  a Clear as readily as a mark, which is what makes a mis-tap cheap
                  rather than merely recoverable in principle. */}
              {!readOnly && isRunning && onUndoLastObservation
                && (runningState?.observations.length ?? 0) > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); onUndoLastObservation(); }}
                  disabled={runPending}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-300 dark:border-blue-700 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Undo last
                </button>
              )}

              {/* Redo. Only when there is a branch to restore — acting after an undo
                  discards it, so an always-visible control would usually do nothing. */}
              {!readOnly && isRunning && onRedoLastObservation
                && (runningState?.redoStack.length ?? 0) > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRedoLastObservation(); }}
                  disabled={runPending}
                  className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-300 dark:border-blue-700 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Redo2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Redo
                </button>
              )}

              {/* Anchors at risk are warned about ON THEIR OWN ROW (see `runRow`), not
                  listed here — matching a name in a banner to a row further down the
                  page is work the operator should not be doing mid-service. Still a
                  warning and never a block (plan §8 Q2). */}

            </div>
          );
        })()}

        {/* Running-state write failure */}
        {!readOnly && runError && (
          <div role="alert" className="flex items-start justify-between gap-3 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
            <span className="text-xs sm:text-sm text-red-700 dark:text-red-300">{runError}</span>
            <button onClick={onClearRunError} aria-label="Dismiss error" className="text-red-600 dark:text-red-400 hover:opacity-70 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Add Element Form */}
        {!readOnly && editMode && showAddForm && (
          <div className="bg-gray-200/30 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-300 dark:border-gray-600">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-500 dark:text-green-400" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Add Element</h3>
              </div>
            </div>
            <div className="space-y-4">
              <input type="text" placeholder="Description (e.g., Worship, Message)" value={newElementData.description || ''}
                onChange={(e) => setNewElementData({ ...newElementData, description: e.target.value })}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Duration (MM:SS)</label>
                  <div className="flex items-center gap-1">
                    <input type="number" min="0" value={Math.floor((newElementData.duration || 0) / 60)}
                      onChange={(e) => { const m = parseInt(e.target.value) || 0; const s = (newElementData.duration || 0) % 60; setNewElementData({ ...newElementData, duration: m * 60 + s }); }}
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                    <span className="text-gray-400 text-lg font-bold">:</span>
                    <input type="number" min="0" max="59" value={(newElementData.duration || 0) % 60}
                      onChange={(e) => { const s = Math.min(59, Math.max(0, parseInt(e.target.value) || 0)); const m = Math.floor((newElementData.duration || 0) / 60); setNewElementData({ ...newElementData, duration: m * 60 + s }); }}
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Anchor offset (optional)</label>
                  <div className="flex space-x-2">
                    <input type="number" placeholder="Min"
                      value={newElementData.anchorOffset !== null && newElementData.anchorOffset !== undefined ? Math.floor(newElementData.anchorOffset / 60) : ''}
                      onChange={(e) => { const v = e.target.value; if (v === '' && ((newElementData.anchorOffset ?? 0) % 60) === 0) setNewElementData({ ...newElementData, anchorOffset: null }); else { const m = parseInt(v) || 0; const s = (newElementData.anchorOffset ?? 0) % 60; setNewElementData({ ...newElementData, anchorOffset: m * 60 + s }); } }}
                      className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                    <input type="number" min="0" max="59" placeholder="Sec"
                      value={newElementData.anchorOffset !== null && newElementData.anchorOffset !== undefined ? (newElementData.anchorOffset % 60) : ''}
                      onChange={(e) => { const v = e.target.value; if (v === '' && Math.floor((newElementData.anchorOffset ?? 0) / 60) === 0) setNewElementData({ ...newElementData, anchorOffset: null }); else { const s = Math.min(59, Math.max(0, parseInt(v) || 0)); const m = Math.floor((newElementData.anchorOffset ?? 0) / 60); setNewElementData({ ...newElementData, anchorOffset: m * 60 + s }); } }}
                      className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Leave anchor offset empty for chained elements (start after previous ends). Set a value to anchor at a specific time from occurrence start.</p>
              <button onClick={handleAddNewElement} className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all flex items-center justify-center">
                <Plus className="w-4 h-4 mr-2" /> Add Element
              </button>
            </div>
          </div>
        )}

        {/* Tip (edit mode only) */}
        {!readOnly && editMode && sortedElements.length > 1 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">Tip: drag an element onto another to group them; drag to the top/bottom edge to reorder.</p>
        )}

        {/* Elements List (tree) */}
        {sortedElements.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">No elements yet</div>
            <div className="text-gray-500 text-sm">Add your first element using the button above</div>
          </div>
        ) : (
          <div className="space-y-2">
            {tree.map((element) => (isGroup(elements, element) ? renderGroup(element) : renderCard(element, false)))}
          </div>
        )}
      </div>

      {/* Sticky Mark bar.
          The run sheet is longer than a phone screen, so the single control the
          operator needs most was reachable only by scrolling back to the header. This
          keeps it under the thumb without occupying the list. Owner-only, live-only,
          and it disappears the moment there is nothing left to mark. */}
      {!readOnly && isRunning && nextToMark && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur px-3 sm:px-4 pt-2.5"
          style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="mx-auto flex max-w-3xl items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[0.65rem] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                Next
              </div>
              <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                {nextToMark.description}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleMark(nextToMark.id); }}
              disabled={runPending}
              aria-label={`Mark ${nextToMark.description} as started`}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-white transition-all duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-blue-600"
            >
              <Flag className="h-4 w-4" />
              <span className="text-sm font-semibold">Mark</span>
            </button>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default TimeElementList;
